import React, { useEffect, useState } from "react";

import SectionHeader from "@cayuse/shared-react.components.section-header";
import { useAuth } from "@cayuse/shared-react.utils.auth";
import { api } from "services";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import IconButton from "@cayuse/shared-react.components.icon-button";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";

import SPLink from "components/SPLink";
import Table from "components/Table";
import ImportExportButtons from "containers/Form/List/ImportExportButtons";
import { FORMS_BASE_ROUTE } from "globals/constants";
import { useDictionary } from "hooks";
import { notifyError } from "utils";

import styles from "./styles.css";


const FormListView = () => {
	const [ formModels, setFormModels ] = useState([]);
	const [ loading, setLoading ] = useState(true);
	const [ shouldShowRulesButton ] = useState(false);
	const { isAuthenticated } = useAuth();
	const dictionary = useDictionary("formManager");

	const load = async () => {
		try {
			const response = await Promise.all([
				api.form.getAllModels(),
				api.form.getFormDeployments(),
			]);

			let formDeployments = {};
			for (const formDeployment of response[1]) {
				formDeployments[formDeployment.formKey] = formDeployment.deploymentTime;
			}
			const loadedFormModels = response[0];
			for (const formModel of loadedFormModels) {
				formModel.deploymentTime = formDeployments[formModel.modelKey];
			}
			setFormModels(loadedFormModels);

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);

		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (isAuthenticated) {
			load();
		}
	}, [ isAuthenticated ]);

	if (loading) {
		return (
			<div>
				<SectionHeader
					header={`${dictionary.heading}`}
					breadcrumbs={<span>{dictionary.breadcrumbRoot || "Forms"}</span>}
					/>
				<LoadingIndicator />
			</div>
		);
	}

	return (
		<div>
			<SectionHeader
				header={`${dictionary.heading}`}
				breadcrumbs={<span>{dictionary.breadcrumbRoot || "Forms"}</span>}
			/>
			<BasePadding>
				<Table captionText="Form Manager">
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>
								{dictionary.tableHeadings?.type || "Type"}
							</Table.HeaderCell>
							<Table.HeaderCell>
								{dictionary.tableHeadings?.published || "Published"}
							</Table.HeaderCell>
							<Table.HeaderCell/>
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{formModels.map(formModel => {
							const formModelId = formModel.modelId;
							const builderRoute = `${FORMS_BASE_ROUTE}/${formModelId}`;

							return (
								<Table.Row key={formModel.modelId}>
									<Table.Cell>
										<SPLink to={builderRoute}>
											{formModel.name}
										</SPLink>
									</Table.Cell>
									<Table.Cell>
										{formModel.deploymentTime
											? <FormattedDatetime date={formModel.deploymentTime} />
											: (dictionary.deploymentTimeNever || "Never")
										}
									</Table.Cell>
									<Table.Cell className={styles.actionCell}>
										<ImportExportButtons formModel={formModel}/>

										{shouldShowRulesButton &&
										<SPLink
											className={styles.rulesButton}
											to={`${FORMS_BASE_ROUTE}/${formModelId}/rules`}
										>
											<IconButton primary size="small" icon="book"/>
										</SPLink>
										}
									</Table.Cell>
								</Table.Row>
							);
						})}
					</Table.Body>
				</Table>
			</BasePadding>
		</div>
	);
};

export default FormListView;
